import React, { useState } from "react";
import { AxiosError } from "axios";
import { Box } from "@mui/material";
import { useListAndSearchAllBookings } from "@bn/api/apiv1";
import { axiosErrorToApiException, DepotSynchronizationLog } from "@api";
import { useGetAccount } from "@api/v1/hooks/useAccounts";
import { useRollbackDepotSynchronizationLog } from "@api/v1/hooks/useDepotSynchronizationLog";
import { SynchronizationLogItem } from "@components/SynchronizationLog/SynchronizationLogItem";
import { BOOKINGS_COMPATIBILITY_VERSION } from "@constants";
import { LoadingProgressBar } from "../common/progress/LoadingProgressBar";
import { toDisplayFormat } from "./convertLotItemsToDisplayFormat";
import { AccountHeaderWithRollback } from "./AccountHeaderWithRollback";

interface Props {
  depotSynchronizationLog: DepotSynchronizationLog;
}

export const SynchronizationLog = ({ depotSynchronizationLog }: Props) => {
  const [rollbackError, setRollbackError] = useState<
    ApiException | undefined
  >();

  const bookingIds =
    depotSynchronizationLog.logItems?.reduce<number[]>(
      (acc, it) => (it.bookingId ? [...acc, it.bookingId] : acc),
      []
    ) || [];

  const { data: depot, isFetching: isDepotLoading } = useGetAccount(
    depotSynchronizationLog.accountId
  );
  const { data: bookingList, isFetching: areBookingsLoading } =
    useListAndSearchAllBookings(
      {
        id: bookingIds,
        enumCompatibilityVersion: BOOKINGS_COMPATIBILITY_VERSION,
      },
      { enabled: bookingIds.length > 0 }
    );
  const {
    mutateAsync: rollbackDepotSynchronizationLog,
    isPending: rollbackInProgress,
  } = useRollbackDepotSynchronizationLog();

  const handleRollback = () => {
    if (!depotSynchronizationLog?.id) return;
    if (rollbackInProgress || !!depotSynchronizationLog.rolledBackAt) return;
    rollbackDepotSynchronizationLog({
      id: depotSynchronizationLog.id,
    }).catch((ex) => {
      const err = axiosErrorToApiException(ex as AxiosError);
      setRollbackError(err);
    });
  };

  const hasUpdates =
    depotSynchronizationLog.logItems &&
    depotSynchronizationLog.logItems.length > 0;

  if (isDepotLoading || (bookingIds.length > 0 && areBookingsLoading)) {
    return <LoadingProgressBar />;
  }

  const logItems = toDisplayFormat(
    depotSynchronizationLog?.logItems,
    bookingList?.bookings
  );

  return (
    <Box>
      <AccountHeaderWithRollback
        onRollback={handleRollback}
        name={depot?.name || "N/A"}
        iban={depot?.iban}
        date={depotSynchronizationLog.executedAt}
        showRollbackLink={hasUpdates}
        isRollbackLoading={rollbackInProgress}
        rollbackErrorMessage={rollbackError?.message}
        noData={logItems.length === 0}
      />

      {Boolean(logItems.length) && (
        <Box display="flex" flexDirection="column" gap={6} mt={6}>
          {logItems.map((item) => {
            return (
              <SynchronizationLogItem
                key={`logItem-${item.id}`}
                logItem={item}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
